import * as moment from 'moment';
import { createSelector, createFeatureSelector } from '@ngrx/store';
import { State } from './esg.reducers';
import { EsgDataResponse, EsgFilter } from './esg.models';
import { DatesInterval } from '../../global/interfaces';
import { getYearsDropdownValues, getMonthsDropdownValues } from '../../global/dates';
import * as lodash from 'lodash';
import { isNullOrUndefined } from 'util';

export const selectEsgState = createFeatureSelector<State>('esg');

export const selectEsgLoading = createSelector(selectEsgState, (state: State) => state.loading);

export const selectEsgFilter = createSelector(selectEsgState, (state: State) => state.filter);

export const selectEsgYearFilter = createSelector(selectEsgFilter, (state?: EsgFilter) => state ? state.year : undefined);

export const selectEsgMonthFilter = createSelector(selectEsgFilter, (state?: EsgFilter) => state ? state.month : undefined);

export const selectEsgHoldingDate = createSelector(selectEsgFilter, (filter?: EsgFilter) => {
    if (!filter || filter.year === undefined || filter.month === undefined) {
        return undefined;
    }
    return moment.utc({ year: filter.year, month: filter.month }).endOf('month').format('YYYY-MM-DD');
});

export const selectEsgDates = createSelector(selectEsgState, (state?: State) => state ? state.dates : undefined);

export const selectEsgDatesMin = createSelector(selectEsgDates, (dates?: DatesInterval) =>
    dates ? moment.utc(dates.min) : undefined
);

export const selectEsgDatesMax = createSelector(selectEsgDates, (dates?: DatesInterval) =>
    dates ? moment.utc(dates.max) : undefined
);

export const selectEsgYears = createSelector(
    selectEsgDatesMin,
    selectEsgDatesMax,
    (min?: moment.Moment, max?: moment.Moment) =>
        getYearsDropdownValues(min, max)

);

export const selectEsgMonths = createSelector(
    selectEsgYearFilter,
    selectEsgDatesMin,
    selectEsgDatesMax,
    (year?: number, min?: moment.Moment, max?: moment.Moment) =>
        getMonthsDropdownValues(year, min, max)

);
export const selectEsgSort = createSelector(selectEsgState, (state: State) => state.sort);

export const selectEsgResponse = createSelector(selectEsgState, (state: State) => state.data);

export const selectEsgData = createSelector(selectEsgResponse, (state: EsgDataResponse) => {
    if (!isNullOrUndefined(state) && !isNullOrUndefined(state.esgData) && state.esgData.length > 0) {
        return state.esgData;
    }
});

export const selectEsgDate = createSelector(selectEsgResponse, (state: EsgDataResponse) => {
    if (!isNullOrUndefined(state) && !isNullOrUndefined(state.esgDate) && state.esgDate.length > 0) {
        return state.esgDate;
    }
});

export const selectEsgDiversityAndInclusion = createSelector(selectEsgData, (state: any) =>
(!isNullOrUndefined(state) && state.length > 0 ? state.map((record) =>
({
    diBoardDiversityFund: record.diBoardDiversity.fund,
    diBoardDiversityBenchmark: record.diBoardDiversity.benchmark,
    name: record.fund.name
})
) : []
));

export const selectEsgModernSlavery = createSelector(selectEsgData, (state: any) =>
(!isNullOrUndefined(state) && state.length > 0 ? state.map((record) =>
({
    name: record.fund.name,
    companiesRedFlag: record.modernSlavery.num,
    porttfolioPercentRedFlag: record.modernSlavery.percent
})
) : []
));

export const selectEsgProxyVoting = createSelector(selectEsgData, (state: any) =>
(!isNullOrUndefined(state) && state.length > 0 ? state.map((record) =>
({
    name: record.fund.name,
    meetingsVotedNumAus: record.votings.meetingsVotedNumAus,
    meetingsVotedPercentAus: record.votings.meetingsVotedPercentAus,
    meetingsVotedNumGlobalExAus: record.votings.meetingsVotedNumGlobalExAus,
    meetingsVotedPercentGlobalExAus: record.votings.meetingsVotedPercentGlobalExAus,
    votesAgainstManagementRecPercentGlobal: record.votings.votesAgainstManagementRecPercentGlobal,
    supervotesNumGlobal: record.votings.supervotesNumGlobal
})
) : []
));

export const selectEsgAnalyticsClimateTransitionDataCarbonIntensity = createSelector(selectEsgData, (state: any) =>
(!isNullOrUndefined(state) && state.length > 0 ? state.map((record) =>
({
    name: record.fund.name,
    dgy: record.carbonIntensity.dgy,
    lgy: record.carbonIntensity.lgy,
    gyib: record.carbonIntensity.gyib,
    ib: record.carbonIntensity.ib,
    gnib: record.carbonIntensity.gnib,
    lgn: record.carbonIntensity.lgn,
    dgn: record.carbonIntensity.dgn
})
) : []
));

export const selectEsgAnalyticsClimateTransitionCarbonWeight = createSelector(selectEsgData, (state: any) =>
(!isNullOrUndefined(state) && state.length > 0 ? state.map((record) =>
({
    name: record.fund.name,
    dgy: record.carbonWeight.dgy,
    lgy: record.carbonWeight.lgy,
    gyib: record.carbonWeight.gyib,
    ib: record.carbonWeight.ib,
    gnib: record.carbonWeight.gnib,
    lgn: record.carbonWeight.lgn,
    dgn: record.carbonWeight.dgn,
    notAssessed: record.carbonWeight.notAssessed
})
) : []
));

export const selectEsgRatings = createSelector(selectEsgData, (state: any) =>
(!isNullOrUndefined(state) && state.length > 0 ? state.map((record) =>
({
    name: record.fund.name,
    allocation: record.allocation,
    esgRating: {
        ratingFund: {
            currentYear: record.esg.ratingFund.currentYear,
            lastYear: record.esg.ratingFund.lastYear,
            secondLastYear: record.esg.ratingFund.secondLastYear
        },
        ratingUniverse: {
            currentYear: record.esg.ratingUniverse.currentYear,
            lastYear: record.esg.ratingUniverse.lastYear,
            secondLastYear: record.esg.ratingUniverse.secondLastYear
        }
    }
})
) : []
));

export const selectEsgCarbonFootPrint = createSelector(selectEsgData, (state: any) =>
(!isNullOrUndefined(state) && state.length > 0 ? state.map((record) =>
({
    name: record.fund.name,
    carbonFootprint: {
        footprintFund: {
            currentYear: record.carbonFootprint.footprintFund.currentYear,
            lastYear: record.carbonFootprint.footprintFund.lastYear,
            secondLastYear: record.carbonFootprint.footprintFund.secondLastYear
        },
        footprintBenchmark: {
            currentYear: record.carbonFootprint.footprintBenchmark.currentYear,
            lastYear: record.carbonFootprint.footprintBenchmark.lastYear,
            secondLastYear: record.carbonFootprint.footprintBenchmark.secondLastYear
        }
    }
})
) : []
));

export const selectEsgSdgData = createSelector(selectEsgData, (state: any) =>
(!isNullOrUndefined(state) && state.length > 0 ? state.map((record) =>
({
    name: record.fund.name,
    level1: {
        overAllFund: record.sdg.overAllFund,
        overAllBenchmark: record.sdg.overAllBenchmark,
    },
    level2: {
        social: {
            socialFund: record.sdg.socialFund,
            socialBenchmark: record.sdg.socialBenchmark,
        },
        environmental: {
            environmentalFund: record.sdg.environmentalFund,
            environmentalBenchmark: record.sdg.environmentalBenchmark
        }
    },
    level3: {
        social: {
            alleviatingPovertyFund: record.sdg.alleviatingPovertyFund,
            alleviatingPovertyBenchmark: record.sdg.alleviatingPovertyBenchmark,
            attainingGenderEqualityFund: record.sdg.attainingGenderEqualityFund,
            attainingGenderEqualityBenchmark: record.sdg.attainingGenderEqualityBenchmark,
            combatingHungerFund: record.sdg.combatingHungerFund,
            combatingHungerBenchmark: record.sdg.combatingHungerBenchmark,
            deliveringEducationFund: record.sdg.deliveringEducationFund,
            deliveringEducationBenchmark: record.sdg.deliveringEducationBenchmark,
            ensuringHealthFund: record.sdg.ensuringHealthFund,
            ensuringHealthBenchmark: record.sdg.ensuringHealthBenchmark,
            providingBasicServicesFund: record.sdg.providingBasicServicesFund,
            providingBasicServicesBenchmark: record.sdg.providingBasicServicesBenchmark,
            safeguardingPeaceFund: record.sdg.safeguardingPeaceFund,
            safeguardingPeaceBenchmark: record.sdg.safeguardingPeaceBenchmark
        },
        environmental: {
            conservingWaterFund: record.sdg.conservingWaterFund,
            conservingWaterBenchmark: record.sdg.conservingWaterBenchmark,
            achievingSustainableAgricultureAndForestsFund: record.sdg.achievingSustainableAgricultureAndForestsFund,
            achievingSustainableAgricultureAndForestsBenchmark: record.sdg.achievingSustainableAgricultureAndForestsBenchmark,
            contributingtoSustainableEnergyUseFund: record.sdg.contributingtoSustainableEnergyUseFund,
            contributingToSustainableEnergyUseBenchmark: record.sdg.contributingToSustainableEnergyUseBenchmark,
            mitigatingClimateChangeFund: record.sdg.mitigatingClimateChangeFund,
            mitigatingClimateChangeBenchmark: record.sdg.mitigatingClimateChangeBenchmark,
            optimisingMaterialUseFund: record.sdg.optimisingMaterialUseFund,
            optimisingMaterialUseBenchmark: record.sdg.optimisingMaterialUseBenchmark,
            preservingMarineEcosystemsFund: record.sdg.preservingMarineEcosystemsFund,
            preservingMarineEcosystemsBenchmark: record.sdg.preservingMarineEcosystemsBenchmark,
            preservingTerrestrialEcosystemsFund: record.sdg.preservingTerrestrialEcosystemsFund,
            preservingTerrestrialEcosystemsBenchmark: record.sdg.preservingTerrestrialEcosystemsBenchmark,
            promotingSustainableBuildingsFund: record.sdg.promotingSustainableBuildingsFund,
            promotingSustainableBuildingsBenchmark: record.sdg.promotingSustainableBuildingsBenchmark
        }
    }
})
) : []
));
